import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpointsV2';
import { Heading } from '@/components/common/Heading';
import { Section, StyledHeading } from '@/components/common/MarkUp';
import { stringToId } from '@/components/common/utils';
import { NavigationLink } from '@/components/layout/NavigationBarV2/NavigationLink';
import { StandoutContainer } from '@/components/layout/NavigationBarV2/Standout/StandoutContainer';
import { StandoutCTAList } from '@/components/layout/NavigationBarV2/Standout/StandoutCTA';

import type { HeadingSanityBlockType } from '@/components/common/Heading';
import type { NavigationLinkProps } from '@/components/layout/NavigationBarV2/NavigationLink';
import type { StandoutBlock } from '@/components/layout/NavigationBarV2/Standout/StandoutCTA';

export type BottomNavLink = {
  title: string;
  url: string;
  _type: string;
  tabIndex?: number;
};

export interface SubNavigationColumnProps {
  _type?: string;
  heading?: HeadingSanityBlockType;
  caption?: string;
  links?: NavigationLinkProps[];
  hideTextOnMobile?: boolean;
  bottomLink?: BottomNavLink;
  standoutBlocks?: StandoutBlock[];
  variant?: 'employers' | 'individuals';
  isAnySubNavOpen?: boolean;
  isSubNavOpen?: boolean;
}

export const SubNavigationColumn = ({
  heading,
  caption,
  links,
  hideTextOnMobile = false,
  bottomLink,
  standoutBlocks,
  variant,
  isAnySubNavOpen,
}: SubNavigationColumnProps) => {
  const captionId = (caption && `caption-${stringToId(caption)}`) || null;
  return (
    <Column hasStandoutBlocks={!!standoutBlocks} variant={variant}>
      <ContentWrapper>
        {!!heading && (
          <HeadingWrapper>
            <Heading
              size="heading-sm"
              elementType="paragraph"
              heading={heading}
            />
          </HeadingWrapper>
        )}
        {caption && (
          <Section>
            <Caption size="caption-sm" id={captionId}>
              {caption}
            </Caption>
          </Section>
        )}
        {links?.length > 0 && (
          <ul aria-labelledby={captionId}>
            {links.map((link) => (
              <LinkItem hideTextOnMobile={hideTextOnMobile} key={link.title}>
                <NavigationLink tabIndex={isAnySubNavOpen ? 0 : -1} {...link} />
              </LinkItem>
            ))}
            {bottomLink?.title && (
              <BottomLinkItem key={bottomLink.title}>
                <NavigationLink
                  tabIndex={isAnySubNavOpen ? 0 : -1}
                  {...bottomLink}
                />
              </BottomLinkItem>
            )}
          </ul>
        )}
        {!!standoutBlocks && (
          <StandoutBlockWrapper>
            <StandoutContainer variant={variant}>
              <StandoutCTAList cards={standoutBlocks} variant={variant} />
            </StandoutContainer>
          </StandoutBlockWrapper>
        )}
      </ContentWrapper>
    </Column>
  );
};

const Column = styled.div<{
  hasStandoutBlocks: boolean;
  variant?: 'employers' | 'individuals';
}>`
  width: 100%;
  padding-left: var(--spacing-250);
  padding-right: var(--spacing-250);

  ${screen.sm} {
    padding-left: var(--spacing-400);
    padding-right: var(--spacing-400);
  }

  ${screen.md} {
    padding-top: var(--spacing-500);
    padding-bottom: ${(props) =>
      props.variant ? 'var(--spacing-600)' : 'var(--spacing-400)'};
  }

  &:first-of-type {
    padding-top: var(--spacing-400);
    ${screen.md} {
      border-right: 1px solid var(--colour-border-subtle);
      > div {
        margin-right: 0;
        margin-left: auto;
      }
    }
    ${screen.lg} {
      padding-top: var(--spacing-500);
      padding-right: var(--spacing-500);
    }
    ${screen.xl} {
      padding-left: 0;
      padding-right: 26px;
    }
  }

  &:nth-of-type(2) {
    padding-top: var(--spacing-200);

    > div {
      margin-right: auto;
      margin-left: 0;
    }

    ${screen.md} {
      background-color: var(--colour-white);
      padding-right: var(--spacing-200);
      padding-top: var(--spacing-400);
    }
    ${screen.lg} {
      padding-right: var(--spacing-300);
      padding-top: var(--spacing-500);
      padding-left: var(--spacing-500);
    }
    ${screen.xl} {
      padding-left: 26px;
      padding-right: 26px;
    }
  }

  &:nth-of-type(3) {
    padding-bottom: ${(props) =>
      props.hasStandoutBlocks ? '0' : 'var(--spacing-200)'};
    ${screen.md} {
      padding-top: var(--spacing-400);
      background-color: var(--colour-white);
      padding-bottom: ${(props) =>
        props.variant ? 'var(--spacing-600)' : 'var(--spacing-400)'};
    }
    ${screen.lg} {
      padding-top: var(--spacing-500);
      padding-right: var(--spacing-500);
      padding-left: 0;
    }
    ${screen.xl} {
      padding-right: 0;
      padding-left: 26px;
    }
  }
`;

const ContentWrapper = styled.div`
  ${screen.md} {
    max-width: calc(
      (
          var(--desktop-max-content-width) - var(--spacing-800) - var(
              --spacing-200
            )
        ) / 3
    );
    transition: transform 300ms ease-out;
  }
  ${screen.lg} {
    max-width: calc(
      (
          var(--desktop-max-content-width) - var(--spacing-1000) - var(
              --spacing-300
            )
        ) / 3
    );
  }
`;

const LinkItem = styled.li<{ hideTextOnMobile: boolean }>`
  p {
    ${(props) => (props.hideTextOnMobile ? 'display: none' : '')};
    ${screen.md} {
      display: inline-block;
    }
  }
`;

const HeadingWrapper = styled.div`
  margin-bottom: var(--spacing-200);
`;

const StandoutBlockWrapper = styled.div`
  display: none;

  ${screen.md} {
    display: block;
  }
`;

const BottomLinkItem = styled.li`
  border-top: 1px solid var(--colour-border-subtle);
  margin-top: var(--spacing-200);
`;

const Caption = styled(StyledHeading)`
  color: var(--colour-deep-space-700);
`;
